import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, ShieldCheck, TrendingUp, Check, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Calculator from "@/components/Calculator";
import ContactBar from "@/components/ContactBar";
import FunnelLinks from "@/components/FunnelLinks";
import Footer from "@/components/Footer";
import StickyCTA from "@/components/StickyCTA";
import CouponBanner from "@/components/CouponBanner";
import { funnelBySlug, FUNNELS, track, scrollToId } from "@/lib/api";

const TONE = {
  fear: { icon: AlertTriangle, accent: "text-red-400", border: "border-red-500/40", bg: "bg-red-500/5", glow: "shadow-[0_0_50px_rgba(239,68,68,0.15)]" },
  trust: { icon: ShieldCheck, accent: "text-gold", border: "border-gold/40", bg: "bg-gold/5", glow: "gold-glow" },
  strategy: { icon: TrendingUp, accent: "text-emerald-400", border: "border-emerald-500/40", bg: "bg-emerald-500/5", glow: "shadow-[0_0_50px_rgba(16,185,129,0.15)]" },
};

export default function Funnel() {
  const { type } = useParams();
  const f = funnelBySlug(type);

  useEffect(() => {
    if (f) { document.title = `${f.title} | Zateplenia.eu`; track("funnel_view", f.slug); }
  }, [f]);

  if (!f) {
    return (
      <div className="min-h-screen bg-ink text-white flex flex-col items-center justify-center gap-4">
        <h1 className="font-display text-5xl">Stránka nenájdená</h1>
        <Link to="/" className="text-gold underline">Domov</Link>
      </div>
    );
  }
  const t = TONE[f.tone];
  const Icon = t.icon;

  return (
    <div>
      <Header />
      <section className="relative min-h-screen flex items-center overflow-hidden pt-24">
        <div className="absolute inset-0 z-0 opacity-40">
          <img src="https://images.unsplash.com/photo-1628012209120-d9db7abf7eab?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="w-full h-full object-cover grayscale" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/90 to-ink/40" />
        </div>
        <div className="relative z-10 max-w-[1100px] mx-auto px-5 md:px-10 py-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className={`inline-flex items-center gap-2 ${t.accent} font-mono2 uppercase tracking-[0.3em] text-xs mb-6`}>
              <Icon className="w-4 h-4" /> {f.kicker}
            </div>
            <h1 className="font-display text-white leading-[0.9] text-5xl sm:text-7xl lg:text-8xl max-w-4xl">{f.title}</h1>
            <p className="text-white/70 text-lg md:text-xl max-w-2xl mt-6 leading-relaxed">{f.subtitle}</p>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
              {f.bullets.map((b, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.1 }}
                  className={`p-5 border ${t.border} ${t.bg}`}>
                  <Check className={`w-5 h-5 ${t.accent} mb-3`} />
                  <p className="text-white/80 text-sm leading-snug">{b}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center">
              <button onClick={() => scrollToId("#kalkulacka")} data-testid="funnel-cta"
                className={`group bg-gold text-ink px-8 py-4 font-bold uppercase tracking-wider flex items-center justify-center gap-3 hover:bg-gold-light transition-colors ${t.glow}`}>
                {f.cta} <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <span className="text-white/50 text-sm">alebo nás rovno kontaktujte:</span>
            </div>
            <div className="mt-4"><ContactBar context={`funnel-${f.slug}`} /></div>
            <div className="mt-6 max-w-2xl"><CouponBanner context={`funnel-${f.slug}`} /></div>
          </motion.div>
        </div>
      </section>

      <Calculator presetDistrict="" />

      <section className="bg-ink-2 py-20 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-6">Ešte zvažujete? Vyberte, čo vás najviac vystihuje</p>
          <FunnelLinks from={`funnel-${f.slug}`} />
        </div>
      </section>
      <Footer />
      <StickyCTA context={`funnel-${f.slug}`} />
    </div>
  );
}
